import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr, fileUrl } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { AlertTriangle, MapPin, Wrench, CheckCircle2 } from "lucide-react";
import { IssueToTaskDialog } from "@/components/IssueToTaskDialog";

const SEV = { low: { l: "Χαμηλή", c: "bg-[#EEF0EA] text-[#6E7A63]" }, medium: { l: "Μεσαία", c: "bg-[#F3EADF] text-[#9A6A3A]" }, high: { l: "Υψηλή", c: "bg-[#F6E2DD] text-[#A1493A]" } };
const STATUS = { open: "Ανοιχτή", in_progress: "Σε εξέλιξη", resolved: "Επιλύθηκε" };
const CAT = { plumbing: "Υδραυλικά", electrical: "Ηλεκτρικά", ac: "Κλιματισμός", pool: "Πισίνα", appliance: "Συσκευή", other: "Άλλο" };
const AREA = { bedroom: "Υπνοδωμάτιο", bathroom: "Μπάνιο", kitchen: "Κουζίνα", living: "Σαλόνι", outdoor_pool: "Εξωτερική πισίνα", terrace: "Βεράντα / Μπαλκόνι", garden: "Κήπος", entrance: "Είσοδος", other: "Άλλο" };

export default function FieldOpsIssues() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("");
  const [toTask, setToTask] = useState(null);
  const { data: issues = [] } = useQuery({
    queryKey: ["fo-issues", filter],
    queryFn: async () => (await api.get(`/fieldops/issues${filter ? `?status=${filter}` : ""}`)).data,
  });
  const upd = useMutation({
    mutationFn: async ({ id, status }) => (await api.put(`/fieldops/issues/${id}`, { status })).data,
    onSuccess: () => { toast.success("Ενημερώθηκε"); qc.invalidateQueries({ queryKey: ["fo-issues"] }); },
    onError: (e) => toast.error(apiErr(e)),
  });

  return (
    <div className="space-y-5" data-testid="fo-issues-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Εργασίες Πεδίου</p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1 inline-flex items-center gap-2"><AlertTriangle className="h-7 w-7" /> Αναφορές Βλαβών</h1>
      </div>
      <div className="flex gap-2">
        {[["", "Όλες"], ["open", "Ανοιχτές"], ["in_progress", "Σε εξέλιξη"], ["resolved", "Επιλυμένες"]].map(([k, l]) => (
          <button key={k} data-testid={`issue-filter-${k || "all"}`} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-full text-sm border ${filter === k ? "bg-[#45423C] text-white border-[#45423C]" : "border-[#E3DED4] text-[#6F6A60] bg-white"}`}>{l}</button>
        ))}
      </div>
      <div className="space-y-3">
        {issues.map((it) => (
          <div key={it.id} data-testid={`issue-${it.id}`} className="rounded-2xl bg-white border border-[#E3DED4] p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-[#45423C]">{it.area ? `${CAT[it.category] || it.category} · ${AREA[it.area] || it.area}` : it.title}</span>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${SEV[it.severity]?.c}`}>{SEV[it.severity]?.l}</span>
                </div>
                <div className="text-xs text-[#8A8577] mt-1 inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {it.unit_name} · {it.reported_by_name} · {(it.created_at || "").slice(0, 10)}</div>
                {it.description && <p className="text-sm text-[#6F6A60] mt-2">{it.description}</p>}
                {it.photos?.length > 0 && <div className="flex flex-wrap gap-2 mt-2">{it.photos.map((id) => <a key={id} href={fileUrl(id)} target="_blank" rel="noreferrer"><img src={fileUrl(id)} alt="" className="h-20 w-20 object-cover rounded-lg border border-[#E3DED4]" /></a>)}</div>}
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <select data-testid={`issue-status-${it.id}`} value={it.status} onChange={(e) => upd.mutate({ id: it.id, status: e.target.value })} className="rounded-lg border border-[#E3DED4] px-2 py-1.5 text-sm bg-white">
                  {Object.entries(STATUS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                {it.task_id ? (
                  <span data-testid={`issue-has-task-${it.id}`} className="inline-flex items-center gap-1 text-xs text-[#6E7A63] font-semibold"><CheckCircle2 className="h-3.5 w-3.5" /> Εργασία δημιουργήθηκε</span>
                ) : (
                  <button data-testid={`issue-to-task-${it.id}`} onClick={() => setToTask(it)}
                    className="inline-flex items-center gap-1 rounded-lg border border-[#E3DED4] px-2 py-1.5 text-xs font-semibold text-[#45423C] hover:border-[#B9AE9A]">
                    <Wrench className="h-3.5 w-3.5" /> Σε εργασία
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        {issues.length === 0 && <p className="text-sm text-[#6F6A60]">Καμία αναφορά.</p>}
      </div>
      {toTask && <IssueToTaskDialog issue={toTask} onClose={() => setToTask(null)} />}
    </div>
  );
}
